import {
  Apple,
  Auth,
  ChevronRight,
  Eye,
  Google,
  LockGrey,
  MailGrey,
  X,
} from "@/components/bnp-icons";
import { Button } from "@/components/bnp-ui";
import { TextInput } from "@mantine/core";
import AuthWrapper from "@/components/layouts/AuthWrapper";
import Link from "next/link";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { AuthService } from "@/services";
import { useMutation } from "@tanstack/react-query";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { toast } from "react-toastify";
import { track } from "@/utils/track";
import parseHttpError from "@/utils/parseHttpError";
import { useRef, useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { GoogleLogin } from "@react-oauth/google";
import AppleSignin from "react-apple-signin-auth";
import { useAuthRedirect } from "@/hooks/useAuthRedirect";
import { useAuth } from "@/contexts/AuthContext";

const defaultValues = {
  email: "",
  password: "",
  recaptchaToken: "",
};

const validationSchema = yup.object().shape({
  email: yup.string().email("Enter a valid email").required("Enter your email"),
  password: yup.string().required("Enter your password"),
});

const SignIn = () => {
  const RecaptchaComponent = ReCAPTCHA as unknown as React.FC<any>;
  const router = useRouter();
  const { user, status, refreshUser, login } = useAuth();
  const { navigateAfterAuth } = useAuthRedirect();
  const recaptchaRef = useRef(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // If the user is already authenticated and arrived with a return target,
  // skip the login form and go straight back to where they came from.
  useEffect(() => {
    if (status === "loading" || status === "idle") return;
    if (router.query.redirect && user) {
      navigateAfterAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, user, router.query.redirect]);

  const {
    getValues,
    setValue,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  /*
  const handleRecaptchaChange = (value: any) => {
    setValue("recaptchaToken", value);
    setRecaptchaError("");
  };
  */

  const EyeIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

  const loginRequest = async () => {
    const values = getValues();
    await login({
      email: values.email.trim(),
      password: values.password.trim(),
    });
  };

  const getLoginErrorMessage = (message: string) => {
    const lower = message.toLowerCase();
    if (
      lower.includes("realtor not found") ||
      lower.includes("realtor doesn't exist") ||
      lower.includes("realtor does not exist")
    ) {
      return "Your account setup is incomplete. Please sign up again to complete your registration.";
    }
    return message;
  };

  const redirectToVerifyEmail = (email: string) => {
    const encodedEmail = btoa(email);
    router.push(`/auth/verify?token=${encodedEmail}`);
  };

  const redirectToOnboarding = (email: string) => {
    const encodedEmail = btoa(email);
    router.push(`/auth/onboarding?token=${encodedEmail}`);
  };

  const handleAuthError = (error: any, email: string) => {
    //@ts-ignore
    const msg = getLoginErrorMessage(parseHttpError(error).message);
    if (msg === "email_not_verified") {
      toast.info(
        "Your email has not been verified. Please verify your email to continue.",
      );
      AuthService.resendOTP({ email })
        .then(() => {
          toast.success("A new verification code has been sent to your email.");
        })
        .catch(() => {
          toast.error("Failed to resend verification code");
        });
      redirectToVerifyEmail(email);
      return;
    }
    if (msg === "onboarding_pending") {
      toast.info(
        "Your profile setup is incomplete. Please complete your onboarding to continue.",
      );
      redirectToOnboarding(email);
      return;
    }
    toast.error(msg);
    if (msg.includes("sign up again")) {
      setTimeout(() => router.push("/auth/sign-up"), 3000);
    }
  };

  const mutation: any = useMutation({
    mutationFn: loginRequest,
    onSuccess: () => {
      track("user_logged_in", { method: "password" });
      navigateAfterAuth();
    },
    onError: (error: any) => {
      handleAuthError(error, getValues().email);
    },
  });

  const onSubmit = () => mutation.mutate();

  const handleGoogleSuccess = async (credentialResponse: any) => {
    console.log(credentialResponse);
    try {
      await AuthService.googleLogin({
        token: credentialResponse.credential,
      });

      await refreshUser();
      track("user_logged_in", { method: "google" });
      navigateAfterAuth();
    } catch (error) {
      handleAuthError(error, credentialResponse?.profile?.email || "");
    }
  };

  const handleAppleSuccess = async (response: any) => {
    console.log("Apple Sign-In Success:", response);
    try {
      // Apple Sign In returns authorization.id_token which contains the user info
      const appleResponse = await AuthService.appleLogin({
        //@ts-ignore
        id_token: response.authorization.id_token,
        code: response.authorization.code,
        state: response.authorization.state,
      });

      await refreshUser();
      track("user_logged_in", { method: "apple" });
      navigateAfterAuth();
    } catch (error) {
      handleAuthError(error, response?.user?.email || "");
    }
  };

  return (
    <AuthWrapper
      footerText={"New to FindNigeriaProperties?"}
      footerBtnText={"Get started"}
      footerLink={"/auth/sign-up"}
      footerIcon={<ChevronRight />}
    >
      <div className="flex flex-col w-[100%]">
        <div className="flex flex-col items-center gap-4">
          <Auth />
          <div className="flex flex-col gap-1 items-center">
            <h2 className="text-main-900 font-semibold text-xl">
              Login to your account
            </h2>
            <p className="text-grey-7 text-sm font-normal">
              Enter your details to login.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-8 mt-6 w-[100%]">
          {/*    <div className="cursor-pointer flex items-center justify-center bg-white rounded-[10px] border border-soft-200 w-[100px] lg:w-[143px] h-[40px]">
            <AppleSignin
              authOptions={{
                clientId:
                  process.env.NEXT_PUBLIC_APPLE_CLIENT_ID ||
                  "",
                scope: "email name",
                redirectURI:
                  process.env.NEXT_PUBLIC_APPLE_REDIRECT_URI ||
                  "",
                state: "findnigeriaproperties",
                nonce: "nonce",
                usePopup: true,
              }}
              uiType="dark"
              className="apple-auth-btn"
              noDefaultStyle={false}
              onSuccess={handleAppleSuccess}
              onError={(error: any) => {
                console.error("Apple Sign-In Error:", error);
                toast.error("Apple login failed");
              }}
              skipScript={false}
              render={(props: any) => (
                <button
                  {...props}
                  className="flex items-center justify-center w-full h-full"
                >
                  <Apple />
                </button>
              )}
            />
          </div> */}

          <div className="cursor-pointer flex items-center justify-center bg-white rounded-[10px] border border-soft-200 w-[100px] lg:w-[143px] h-[40px]">
            <GoogleLogin
              onSuccess={(credentialResponse) => {
                console.log(credentialResponse);
                handleGoogleSuccess(credentialResponse);
              }}
              onError={() => {
                console.log("Login Failed");
              }}
            />
          </div>
        </div>
        <div className=" flex items-center justify-center gap-2 text-xs text-grey-7 pt-6">
          <div className=" border-b w-[144px] lg:w-[208px] h-[1px] border-soft-200"></div>
          <div>OR</div>
          <div className=" border-b w-[144px] lg:w-[208px] h-[1px] border-soft-200"></div>
        </div>
        <div className=" w-[100%] mt-6">
          <form
            action={"submit"}
            autoComplete="off"
            className="flex flex-col gap-8"
            onSubmit={(e) => e.preventDefault()}
          >
            <Controller
              name="email"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  type="email"
                  name="email"
                  autoComplete="off"
                  label="Email Address"
                  value={value}
                  withAsterisk
                  placeholder="Email"
                  leftSection={<MailGrey />}
                  size="md"
                  radius={"md"}
                  className="w-[100%]  h-[52px]"
                  onChange={onChange}
                  onBlur={onBlur}
                  error={errors.email?.message}
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="new-password"
                  label="Password"
                  value={value}
                  withAsterisk
                  placeholder="Password"
                  leftSection={<LockGrey />}
                  rightSection={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  }
                  size="md"
                  radius="md"
                  className="w-[100%] h-[52px] text-sm"
                  onChange={onChange}
                  onBlur={onBlur}
                  error={errors.password?.message}
                />
              )}
            />

            {/*
            <div className="flex flex-col items-center gap-2">
              <RecaptchaComponent
                ref={recaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ""}
                onChange={handleRecaptchaChange}
              />
              {(recaptchaError || errors.recaptchaToken) && (
                <span className="text-red-500 text-xs">
                  {recaptchaError || errors.recaptchaToken?.message}
                </span>
              )}
            </div>
  */}

            <Button
              className="bg-bnp-dark text-white text-sm font-extrabold w-[100%] h-[52px] rounded-lg mt-4 "
              isLoading={mutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              Login
            </Button>
          </form>

          <Link href={"/auth/forgot-password"}>
            <p className="text-sm font-medium text-main-900 underline mt-6 text-center">
              Forgot password?
            </p>
          </Link>
        </div>
      </div>
    </AuthWrapper>
  );
};

export default SignIn;
